import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

import { Button } from '@/components/atoms/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { cn } from '@/lib/utils';

export interface DataPaginationProps {
  /** Current page (1-indexed) */
  page: number;
  /** Number of items per page */
  pageSize: number;
  /** Total number of pages */
  totalPages: number;
  /** Total number of items (optional, for display) */
  totalItems?: number;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Available page size options */
  pageSizeOptions?: number[];
  /** Show page size selector */
  showPageSizeSelector?: boolean;
  /** Show total items count */
  showTotalItems?: boolean;
  /** Show page numbers (desktop only) */
  showPageNumbers?: boolean;
  /** Show first/last page buttons */
  showFirstLastButtons?: boolean;
  /** Variant: 'desktop' | 'mobile' | 'auto' */
  variant?: 'desktop' | 'mobile' | 'auto';
  /** Additional class name */
  className?: string;
}

/**
 * Generate page numbers to display with ellipsis
 * Returns array like: [1, 2, 3, '...', 8, 9, 10] or [1, '...', 4, 5, 6, '...', 10]
 */
function generatePageNumbers(
  currentPage: number,
  totalPages: number,
  maxVisible: number = 7
): (number | 'ellipsis')[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const sidePages = Math.floor((maxVisible - 3) / 2); // Pages on each side of current

  // Always show first page
  pages.push(1);

  // Calculate start and end of middle section
  let start = Math.max(2, currentPage - sidePages);
  let end = Math.min(totalPages - 1, currentPage + sidePages);

  // Adjust if we're near the beginning
  if (currentPage <= sidePages + 2) {
    end = Math.min(totalPages - 1, maxVisible - 2);
  }

  // Adjust if we're near the end
  if (currentPage >= totalPages - sidePages - 1) {
    start = Math.max(2, totalPages - maxVisible + 3);
  }

  // Add ellipsis after first page if needed
  if (start > 2) {
    pages.push('ellipsis');
  }

  // Add middle pages
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  // Add ellipsis before last page if needed
  if (end < totalPages - 1) {
    pages.push('ellipsis');
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

export function DataPagination({
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 50],
  showPageSizeSelector = true,
  showTotalItems = true,
  showPageNumbers = true,
  showFirstLastButtons = true,
  variant = 'auto',
  className,
}: DataPaginationProps) {
  const { t } = useTranslation();

  const canPreviousPage = page > 1;
  const canNextPage = page < totalPages;

  // Calculate item range
  const startItem = totalItems ? (page - 1) * pageSize + 1 : 0;
  const endItem = totalItems ? Math.min(page * pageSize, totalItems) : 0;

  // Generate page numbers for desktop
  const pageNumbers = generatePageNumbers(page, totalPages);

  // Don't render if only one page and no items info to show
  if (totalPages <= 1 && !totalItems) {
    return null;
  }

  // Desktop variant
  const renderDesktop = () => (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      {/* Left: Items info */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {showTotalItems && totalItems ? (
          <span>
            {t('pagination.showing')} {startItem}-{endItem} {t('pagination.of')} {totalItems}
          </span>
        ) : (
          <span>
            {t('pagination.page')} {page} {t('pagination.of')} {totalPages}
          </span>
        )}
      </div>

      {/* Right: Controls */}
      <div className="flex items-center gap-4">
        {/* Page size selector */}
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {t('pagination.rowsPerPage')}
            </span>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center gap-1">
          {/* First page button */}
          {showFirstLastButtons && (
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange?.(1)}
              disabled={!canPreviousPage}
              aria-label={t('pagination.firstPage')}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          )}

          {/* Previous button */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange?.(page - 1)}
            disabled={!canPreviousPage}
            aria-label={t('pagination.previousPage')}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page numbers */}
          {showPageNumbers && totalPages > 1 && (
            <div className="flex items-center gap-1">
              {pageNumbers.map((pageNum, index) =>
                pageNum === 'ellipsis' ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="flex h-8 w-8 items-center justify-center text-muted-foreground"
                  >
                    ...
                  </span>
                ) : (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? 'default' : 'outline'}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onPageChange?.(pageNum)}
                    aria-label={`${t('pagination.goToPage')} ${pageNum}`}
                    aria-current={pageNum === page ? 'page' : undefined}
                  >
                    {pageNum}
                  </Button>
                )
              )}
            </div>
          )}

          {/* Next button */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange?.(page + 1)}
            disabled={!canNextPage}
            aria-label={t('pagination.nextPage')}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last page button */}
          {showFirstLastButtons && (
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange?.(totalPages)}
              disabled={!canNextPage}
              aria-label={t('pagination.lastPage')}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  // Mobile variant
  const renderMobile = () => (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      {/* Navigation buttons */}
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange?.(page - 1)}
          disabled={!canPreviousPage}
          className="gap-1"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('pagination.previous')}
        </Button>

        <span className="text-sm font-medium min-w-[80px] text-center">
          {page} / {totalPages}
        </span>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange?.(page + 1)}
          disabled={!canNextPage}
          className="gap-1"
        >
          {t('pagination.next')}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Items info and page size */}
      <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground">
        {showTotalItems && totalItems && (
          <span>
            {totalItems} {t('pagination.items')}
          </span>
        )}

        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap">{t('pagination.show')}</span>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger className="h-8 w-[65px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );

  // Auto variant uses CSS to show/hide
  if (variant === 'auto') {
    return (
      <>
        {/* Desktop (lg+) */}
        <div className="hidden lg:block">{renderDesktop()}</div>
        {/* Mobile/Tablet (<lg) */}
        <div className="lg:hidden">{renderMobile()}</div>
      </>
    );
  }

  return variant === 'desktop' ? renderDesktop() : renderMobile();
}

export default DataPagination;
